/**
 * PlateauSessionSummary.jsx — Résumé de séance plateau pour intégration
 * dans l'outil de gestion de l'auto-école.
 *
 * Deux volets combinés :
 *   1. FIXE — issu de la data du cours (titre, mantra, durée, phase(s) travaillée(s))
 *   2. PERSONNALISÉ AUTO — issu de l'auto-évaluation de l'élève sur la trame
 *      Plateau (localStorage `code2roues:autoeval-plateau-{phaseId}:v1`),
 *      complété par les champs saisis par le moniteur (élève, point fort,
 *      objectif prochain cours, signature).
 *
 * Sortie : bloc Markdown/texte brut copiable, prêt à coller dans
 * Ediser / Packweb / ClickNLearn / carnet numérique.
 *
 * Persistance UX : les champs saisis par le moniteur sont sauvegardés
 * en localStorage sous `code2roues:session-summary-<slug>:v1` pour ne
 * pas être perdus si la page se recharge.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  ClipboardCopy, Check, X, User, Users, CalendarDays, Clock, Target,
  Sparkles, Signature, ChevronDown, ChevronUp, FileText, Download,
} from 'lucide-react';
import { toast } from 'sonner';

const PHASE_META = {
  p1: { code: 'P1', title: 'Découvrir et ressentir',           color: '#22c55e' },
  p2: { code: 'P2', title: 'Coordonner et gagner en précision', color: '#f59e0b' },
  p3: { code: 'P3', title: 'Maîtriser et enchaîner',            color: '#e11d48' },
};

/** Libellés lisibles pour les 12 compétences de la trame plateau (extraits de
 *  `data/plateauProgression.js`). Utilisés pour rendre le résumé même si
 *  l'utilisateur atterrit sur une page sans avoir chargé la trame. */
const EXERCISE_LABELS = {
  // P1
  'position': 'Position de conduite',
  'demarrage-arret': 'Démarrer & s\'arrêter',
  'deplacer-moteur-coupe': 'Déplacer moteur coupé',
  'patinage': 'Point de patinage',
  'braquage': 'Braquage / contre-braquage doux',
  'rapports': 'Passage des rapports',
  'arret-depart-tournant': 'Arrêt / départ en tournant',
  'demi-tour': 'Demi-tour à basse allure',
  // P2
  'lent-14': 'Allure lente (14 sec)',
  'demi-tour-arrete': 'Demi-tour arrêté',
  'demi-tour-lance': 'Demi-tour lancé',
  'repartition-freinage': 'Répartition du freinage',
  // P3
  'contre-braquage-slalom': 'Contre-braquage / slalom',
  'freinage-urgence': 'Freinage d\'urgence',
  'evitement': 'Évitement',
  'demi-tour-passager': 'Demi-tour avec passager',
  'assemblage': 'Assemblage / enchaînement examen',
};

/** Convertit 'ok'|'todo'|'no' en libellé humain pour le Markdown. */
function autoEvalLabel(v) {
  if (v === 'ok')   return '✅ Acquis';
  if (v === 'todo') return '🔁 À retravailler';
  if (v === 'no')   return '⏳ Découvert';
  return '—';
}

/** ISO YYYY-MM-DD du jour, utilisé comme valeur par défaut du champ date. */
function todayISO() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** JJ/MM/AAAA pour l'affichage humain dans le Markdown. */
function formatDateFR(iso) {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  if (!y || !m || !d) return iso;
  return `${d}/${m}/${y}`;
}

/** Charge les auto-évaluations pour une liste de phases. */
function loadAutoEvalForPhases(phaseIds) {
  const merged = {}; // { exId: state }
  const perPhase = {}; // { phaseId: { exId: state } }
  if (typeof window === 'undefined') return { merged, perPhase };
  phaseIds.forEach((phaseId) => {
    const key = `code2roues:autoeval-plateau-${phaseId}:v1`;
    try {
      const raw = JSON.parse(localStorage.getItem(key) || '{}');
      perPhase[phaseId] = {};
      Object.entries(raw).forEach(([exId, val]) => {
        if (val === 'ok' || val === 'todo' || val === 'no') {
          merged[exId] = val;
          perPhase[phaseId][exId] = val;
        }
      });
    } catch (_) { /* skip */ }
  });
  return { merged, perPhase };
}

/**
 * Construit le résumé Markdown complet.
 */
function buildMarkdown({
  courseTitle, courseCode, mantra, subtitle, duree,
  form, selectedPhaseIds, autoEval,
}) {
  const lines = [];
  lines.push(`# Résumé de séance — ${courseTitle}`);
  if (courseCode) lines.push(`_Code séance : **${courseCode}**_`);
  if (subtitle)   lines.push(`_${subtitle}_`);
  lines.push('');

  lines.push('## Informations séance');
  lines.push(`- **Élève :** ${form.eleve || '_(à compléter)_'}`);
  lines.push(`- **Moniteur :** ${form.moniteur || '_(à compléter)_'}`);
  lines.push(`- **Date :** ${formatDateFR(form.date)}`);
  lines.push(`- **Durée :** ${form.duree || duree || '2 heures'}`);
  if (mantra) lines.push(`- **Fil conducteur :** ${mantra}`);
  lines.push('');

  if (selectedPhaseIds.length > 0) {
    lines.push('## Phase(s) pédagogique(s) travaillée(s)');
    selectedPhaseIds.forEach((pid) => {
      const meta = PHASE_META[pid];
      if (meta) lines.push(`- **${meta.code}** · ${meta.title}`);
    });
    lines.push('');
  }

  // Auto-éval : tableau markdown des compétences vues
  const exIds = Object.keys(autoEval);
  if (exIds.length > 0) {
    lines.push('## Compétences vues aujourd\'hui');
    lines.push('| Compétence | Statut |');
    lines.push('|---|---|');
    exIds.forEach((exId) => {
      const label = EXERCISE_LABELS[exId] || exId;
      lines.push(`| ${label} | ${autoEvalLabel(autoEval[exId])} |`);
    });
    lines.push('');
  }

  if (form.pointFort) {
    lines.push('## Point fort du jour');
    lines.push(form.pointFort);
    lines.push('');
  }

  if (form.objectifProchain) {
    lines.push('## Objectif prochain cours');
    lines.push(form.objectifProchain);
    lines.push('');
  }

  lines.push('---');
  lines.push(`_Signature moniteur : **${form.moniteur || '_______________'}**_`);
  lines.push('');
  lines.push('_Résumé généré depuis code2roues.fr — CODE 2 ROUES 2026_');

  return lines.join('\n');
}

export default function PlateauSessionSummary({
  courseSlug,           // ex: 'premiere-plateau-a2'
  courseTitle,          // ex: 'Ma 1ʳᵉ séance plateau A2'
  courseCode,           // ex: 'Plateau n°1'
  mantra,
  subtitle,
  duree,
  defaultPhaseIds = ['p1'], // mapping cours → phases trame
  compact = false,      // rendu inline compact (dans une trame) vs full-width
}) {
  const STORAGE_KEY = `code2roues:session-summary-${courseSlug}:v1`;

  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [form, setForm] = useState({
    eleve: '',
    moniteur: '',
    date: todayISO(),
    duree: duree || '2h',
    pointFort: '',
    objectifProchain: '',
  });
  const [selectedPhaseIds, setSelectedPhaseIds] = useState(defaultPhaseIds);

  // Charge les valeurs sauvegardées à l'ouverture (le moniteur reprend
  // là où il en était s'il est revenu sur la page).
  useEffect(() => {
    try {
      const raw = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
      if (raw.form) setForm((f) => ({ ...f, ...raw.form, date: raw.form.date || todayISO() }));
      if (Array.isArray(raw.selectedPhaseIds) && raw.selectedPhaseIds.length > 0) {
        setSelectedPhaseIds(raw.selectedPhaseIds);
      }
    } catch (_) { /* skip */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persiste à chaque changement
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ form, selectedPhaseIds }));
    } catch (_) { /* skip */ }
  }, [form, selectedPhaseIds, STORAGE_KEY]);

  const { merged: autoEval } = useMemo(
    () => loadAutoEvalForPhases(selectedPhaseIds),
    [selectedPhaseIds, open], // recompute quand on ouvre le panneau
  );

  const markdown = useMemo(
    () => buildMarkdown({
      courseTitle, courseCode, mantra, subtitle, duree,
      form, selectedPhaseIds, autoEval,
    }),
    [courseTitle, courseCode, mantra, subtitle, duree, form, selectedPhaseIds, autoEval],
  );

  const stats = useMemo(() => {
    const values = Object.values(autoEval);
    return {
      total: values.length,
      acquis: values.filter((v) => v === 'ok').length,
      encours: values.filter((v) => v === 'todo').length,
      decouvert: values.filter((v) => v === 'no').length,
    };
  }, [autoEval]);

  const copyMarkdown = async () => {
    try {
      await navigator.clipboard.writeText(markdown);
      setCopied(true);
      toast.success('Résumé copié — colle-le dans ton outil auto-école.');
      setTimeout(() => setCopied(false), 2200);
    } catch (_) {
      toast.error('Copie impossible. Utilise Ctrl+A + Ctrl+C dans la zone de texte.');
    }
  };

  const downloadTxt = () => {
    try {
      const blob = new Blob([markdown], { type: 'text/plain;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const safeDate = form.date || todayISO();
      a.download = `resume-${courseSlug || 'seance'}-${safeDate}.txt`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Résumé téléchargé (.txt).');
    } catch (_) {
      toast.error('Téléchargement impossible sur ce navigateur.');
    }
  };

  const togglePhase = (pid) => {
    setSelectedPhaseIds((prev) =>
      prev.includes(pid) ? prev.filter((p) => p !== pid) : [...prev, pid],
    );
  };

  // Bouton uniquement replié
  if (!open) {
    return (
      <section
        className={`rounded-2xl border border-red-500/30 bg-gradient-to-br from-red-600/10 via-red-500/5 to-transparent p-4 sm:p-5 ${compact ? '' : 'mt-6'} print:hidden`}
        data-testid={`plateau-session-summary-${courseSlug}`}
      >
        <div className="flex flex-wrap items-start gap-3 justify-between">
          <div className="min-w-0 flex-1">
            <div className="inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-red-300 mb-1.5">
              <FileText className="w-3 h-3" /> Résumé auto-école
            </div>
            <h3
              className="text-white font-black text-lg leading-tight"
              style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
            >
              Générer un résumé pour l'outil auto-école
            </h3>
            <p className="text-white/70 text-[12.5px] mt-1 leading-relaxed max-w-2xl">
              Génère un bloc de texte à copier dans Ediser, Packweb, ClickNLearn ou n'importe quel carnet numérique.
              Basé sur la trame du cours <strong className="text-white">+ l'auto-évaluation de l'élève</strong>.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="inline-flex items-center gap-1.5 h-10 px-4 rounded-xl bg-red-600 hover:bg-red-500 text-white text-[12px] font-black uppercase tracking-widest shadow-lg transition"
            data-testid={`btn-open-session-summary-${courseSlug}`}
          >
            <FileText className="w-3.5 h-3.5" /> Ouvrir le résumé
          </button>
        </div>
      </section>
    );
  }

  // Panneau ouvert
  return (
    <section
      className={`rounded-3xl border-2 border-red-500/40 bg-[#141416] p-4 sm:p-6 ${compact ? '' : 'mt-6'} print:hidden`}
      data-testid={`plateau-session-summary-open-${courseSlug}`}
    >
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="min-w-0">
          <div className="inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-red-300 mb-1">
            <FileText className="w-3 h-3" /> Résumé auto-école · {courseCode || courseTitle}
          </div>
          <h3
            className="text-white font-black text-lg sm:text-xl leading-tight"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            Renseigne la séance, on prépare le Markdown copiable
          </h3>
        </div>
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="w-9 h-9 rounded-full bg-white/[0.05] hover:bg-white/[0.12] border border-white/10 text-white/70 flex items-center justify-center transition flex-shrink-0"
          aria-label="Fermer"
          data-testid={`btn-close-session-summary-${courseSlug}`}
        >
          <X size={16} />
        </button>
      </div>

      {/* Champs saisis par le moniteur */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
        <FormField
          icon={User}
          label="Élève"
          value={form.eleve}
          onChange={(v) => setForm((f) => ({ ...f, eleve: v }))}
          placeholder="Prénom Nom"
          testid={`ss-eleve-${courseSlug}`}
        />
        <FormField
          icon={Users}
          label="Moniteur"
          value={form.moniteur}
          onChange={(v) => setForm((f) => ({ ...f, moniteur: v }))}
          placeholder="Nom du moniteur"
          testid={`ss-moniteur-${courseSlug}`}
        />
        <FormField
          icon={CalendarDays}
          label="Date"
          value={form.date}
          onChange={(v) => setForm((f) => ({ ...f, date: v }))}
          type="date"
          testid={`ss-date-${courseSlug}`}
        />
        <FormField
          icon={Clock}
          label="Durée"
          value={form.duree}
          onChange={(v) => setForm((f) => ({ ...f, duree: v }))}
          placeholder="2h"
          testid={`ss-duree-${courseSlug}`}
        />
      </div>

      {/* Sélecteur phase(s) travaillée(s) */}
      <div className="mb-4">
        <div className="text-[10px] font-black uppercase tracking-widest text-white/60 mb-2">
          Phase(s) pédagogique(s) travaillée(s)
        </div>
        <div className="flex flex-wrap gap-2">
          {Object.entries(PHASE_META).map(([pid, meta]) => {
            const active = selectedPhaseIds.includes(pid);
            return (
              <button
                key={pid}
                type="button"
                onClick={() => togglePhase(pid)}
                className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-xl text-[11px] font-black uppercase tracking-widest border transition ${
                  active
                    ? 'text-white border-transparent shadow-lg'
                    : 'bg-white/[0.03] border-white/15 text-white/50 hover:text-white/85 hover:bg-white/[0.06]'
                }`}
                style={active ? { background: meta.color } : {}}
                data-testid={`ss-phase-${courseSlug}-${pid}`}
                aria-pressed={active}
              >
                {meta.code} · {meta.title}
              </button>
            );
          })}
        </div>
        {stats.total > 0 ? (
          <p className="text-emerald-300 text-[11.5px] mt-2 font-semibold">
            Auto-éval détectée : <strong>{stats.total} compétence{stats.total > 1 ? 's' : ''}</strong> ({stats.acquis} acquis · {stats.encours} en cours · {stats.decouvert} découverts)
          </p>
        ) : (
          <p className="text-amber-200/80 text-[11.5px] mt-2 italic">
            Aucune auto-évaluation détectée pour ces phases. L'élève doit d'abord se noter sur la trame Plateau.
          </p>
        )}
      </div>

      {/* Point fort + objectif */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
        <FormField
          icon={Sparkles}
          label="Point fort du jour"
          value={form.pointFort}
          onChange={(v) => setForm((f) => ({ ...f, pointFort: v }))}
          placeholder="Ex : bonne dissociation regard / commandes, allure lente stabilisée…"
          testid={`ss-pointfort-${courseSlug}`}
          multiline
        />
        <FormField
          icon={Target}
          label="Objectif prochain cours"
          value={form.objectifProchain}
          onChange={(v) => setForm((f) => ({ ...f, objectifProchain: v }))}
          placeholder="Ex : consolider le freinage d'urgence, travailler le regard en évitement…"
          testid={`ss-objectif-${courseSlug}`}
          multiline
        />
      </div>

      {/* Aperçu Markdown */}
      <div className="mb-3 rounded-xl border border-white/10 bg-black/40 overflow-hidden">
        <div className="flex items-center justify-between px-3 py-2 bg-white/[0.03] border-b border-white/10">
          <div className="text-[10px] font-black uppercase tracking-widest text-white/60 inline-flex items-center gap-1.5">
            <FileText className="w-3 h-3" /> Aperçu Markdown (copiable / collable)
          </div>
          <div className="text-[10px] text-white/40 tabular-nums">
            {markdown.length} caractères
          </div>
        </div>
        <textarea
          value={markdown}
          readOnly
          rows={12}
          className="w-full bg-transparent px-3 py-2 text-white/85 text-[12px] font-mono leading-relaxed resize-y focus:outline-none"
          data-testid={`ss-markdown-preview-${courseSlug}`}
          onClick={(e) => e.currentTarget.select()}
        />
      </div>

      {/* Actions */}
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={copyMarkdown}
          className="inline-flex items-center gap-1.5 h-10 px-4 rounded-xl bg-red-600 hover:bg-red-500 text-white text-[12px] font-black uppercase tracking-widest shadow-lg transition"
          data-testid={`ss-btn-copy-${courseSlug}`}
        >
          {copied ? <Check size={14} /> : <ClipboardCopy size={14} />}
          {copied ? 'Copié !' : 'Copier le résumé'}
        </button>
        <button
          type="button"
          onClick={downloadTxt}
          className="inline-flex items-center gap-1.5 h-10 px-4 rounded-xl border border-white/15 bg-white/5 hover:bg-white/10 text-white text-[12px] font-black uppercase tracking-widest transition"
          data-testid={`ss-btn-download-${courseSlug}`}
        >
          <Download size={14} /> Télécharger .txt
        </button>
        <button
          type="button"
          onClick={() => window.print()}
          className="inline-flex items-center gap-1.5 h-10 px-4 rounded-xl border border-white/15 bg-transparent hover:bg-white/[0.05] text-white/75 hover:text-white text-[12px] font-black uppercase tracking-widest transition"
          data-testid={`ss-btn-print-${courseSlug}`}
        >
          Imprimer
        </button>
      </div>

      <p className="mt-4 text-[10.5px] text-white/40 leading-relaxed">
        Toutes les saisies sont sauvegardées localement sur cet appareil (pas de cloud). Chaque cours a son propre résumé.
      </p>
    </section>
  );
}

/** Champ de formulaire compact (input ou textarea) avec icône. */
function FormField({ icon: Icon, label, value, onChange, placeholder, type = 'text', multiline = false, testid }) {
  return (
    <label className="block">
      <div className="inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-white/60 mb-1">
        {Icon && <Icon className="w-3 h-3" />} {label}
      </div>
      {multiline ? (
        <textarea
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          rows={2}
          className="w-full bg-white/[0.05] border border-white/10 rounded-lg px-3 py-2 text-white text-[13px] focus:border-red-400 focus:outline-none placeholder:text-white/25 resize-y"
          data-testid={testid}
        />
      ) : (
        <input
          type={type}
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="w-full bg-white/[0.05] border border-white/10 rounded-lg px-3 h-10 text-white text-[13px] font-semibold focus:border-red-400 focus:outline-none placeholder:text-white/25"
          data-testid={testid}
        />
      )}
    </label>
  );
}
